import React from 'react';
import PropTypes from 'prop-types';
import {makeStyles} from 'tss-react/mui';
import {useIntl} from 'react-intl';
import CheckIcon from '@mui/icons-material/Check';

const useStyles = makeStyles()((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'row-reverse',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px 16px',
    gap: 0,
  },
  step: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    position: 'relative',
    zIndex: 1,
  },
  circle: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 700,
    fontFamily: theme.typography.fontFamily,
    border: `2px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.secondary,
    transition: 'all 0.3s ease',
  },
  circleActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  circleCompleted: {
    borderColor: theme.palette.success.main,
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
  },
  checkIcon: {
    fontSize: theme.typography.pxToRem(20),
  },
  label: {
    marginTop: 6,
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 500,
    fontFamily: theme.typography.fontFamily,
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
  },
  labelActive: {
    color: theme.palette.primary.main,
    fontWeight: 700,
  },
  labelCompleted: {
    color: theme.palette.success.main,
    fontWeight: 700,
  },
  connector: {
    width: 60,
    height: 2,
    backgroundColor: theme.palette.divider,
    marginBottom: 20,
    transition: 'background-color 0.3s ease',
    '@media (min-width: 768px)': {
      width: 100,
    },
  },
  connectorCompleted: {
    backgroundColor: theme.palette.success.main,
  },
}));

const STEP_KEYS = [
  {number: 1, labelId: 'sign.stepper.auth'},
  {number: 2, labelId: 'sign.stepper.sign'},
  {number: 3, labelId: 'sign.stepper.done'},
];

const SignProgressStepper = ({activeStep}) => {
  const {classes} = useStyles();
  const intl = useIntl();

  return (
    <div className={classes.container}>
      {STEP_KEYS.map((step, index) => {
        const isCompleted = activeStep > index;
        const isActive = activeStep === index;
        let circleState = '';
        let labelState = '';

        if (isCompleted) {
          circleState = classes.circleCompleted;
          labelState = classes.labelCompleted;
        } else if (isActive) {
          circleState = classes.circleActive;
          labelState = classes.labelActive;
        }

        return (
          <React.Fragment key={step.number}>
            {index > 0 && (
              <div className={`${classes.connector} ${isCompleted ? classes.connectorCompleted : ''}`} />
            )}
            <div className={classes.step}>
              <div className={`${classes.circle} ${circleState}`}>
                {isCompleted ? <CheckIcon className={classes.checkIcon} /> : step.number}
              </div>
              <span className={`${classes.label} ${labelState}`}>
                {intl.formatMessage({id: step.labelId})}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

SignProgressStepper.propTypes = {
  activeStep: PropTypes.number.isRequired,
};

export default SignProgressStepper;
